import { useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  LoadingRegion,
  Notice,
  SEATING_CONFIGURATION_STATES,
  Status,
  presentation,
} from '@gatecheckpro/design-system';
import {
  type OrganizerEvent,
  type SeatingAvailabilitySummary,
  type SeatingConfigurationDocumentView,
  type SeatingInventory,
  type TableSaleMode,
  type VenueMapVersionTopology,
} from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { SeatingCounts, SeatingRoomCanvas, useSeatDetail } from './SeatingRoom.tsx';
import {
  applyToSeats,
  setTableSaleMode,
  useSeatingDraft,
} from '../../lib/seating/useSeatingDraft.ts';
import { eligibilityFor } from '../../lib/seating/eligibility.ts';

/**
 * Configuring what sells, on one night, in one room.
 *
 * Every number on this page is the server's. The overlay is its per-seat
 * projection and the counts are its aggregate — not because the browser could
 * not add up the seats it happens to hold, but because at the sizes this has to
 * work at, the browser deliberately is not holding them all, and a total
 * derived from what it drew would be confidently wrong.
 */
const DRAFT_STATE = {
  persisted: {
    label: 'Saved',
    tone: 'positive' as const,
    glyph: '✓',
    meaning: 'What you are looking at is what GateCheckPro has.',
  },
  unsaved: {
    label: 'Not saved',
    tone: 'caution' as const,
    glyph: '●',
    meaning: 'These changes are in this browser only. Nothing else can see them yet.',
  },
  saving: {
    label: 'Saving',
    tone: 'info' as const,
    glyph: '→',
    meaning: 'Sending the configuration to GateCheckPro.',
  },
  failed: {
    label: 'Not saved',
    tone: 'critical' as const,
    glyph: '✕',
    meaning: 'The save was refused. Your changes are still here.',
  },
  stale: {
    label: 'Someone else saved first',
    tone: 'critical' as const,
    glyph: '⚠',
    meaning:
      'This configuration moved on while you were working. Your changes are still here and will not be written over theirs.',
  },
};

export function SeatingConfigurationEditor({
  base,
  versionPath,
  event,
  eventSessionId,
  loaded,
  summary,
  inventory,
  onChanged,
}: {
  readonly base: string;
  readonly versionPath: string;
  readonly event: OrganizerEvent;
  readonly eventSessionId: string;
  /** The authoritative static document being edited. */
  readonly loaded: SeatingConfigurationDocumentView;
  /** Server-owned counts, for the whole room and every section and table. */
  readonly summary: SeatingAvailabilitySummary;
  /** The capacity set aside for this night, which chairs draw against. */
  readonly inventory: readonly SeatingInventory[];
  readonly onChanged: () => void;
}) {
  const draft = useSeatingDraft(loaded, versionPath);
  const map = useResource<VenueMapVersionTopology>(
    `${base}/venue-map-versions/${loaded.venueMapVersionId}`,
  );
  const command = useCommand<unknown>();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [productId, setProductId] = useState('');

  const lifecycle = presentation(SEATING_CONFIGURATION_STATES, loaded.lifecycle);
  const editable = loaded.lifecycle === 'DRAFT';
  const state = DRAFT_STATE[draft.status];

  const { overlay, requestDetail } = useSeatDetail(versionPath);

  // Eligible *and* provisioned. A product with no capacity set aside for this
  // night has nothing for a chair to draw on, so offering it would only build a
  // configuration that activation must refuse.
  const provisioned = new Set(inventory.map((row) => row.ticketProductId));
  const usableProducts = eligibilityFor(event.ticketProducts, eventSessionId).filter(
    (entry) => entry.eligible && provisioned.has(entry.product.ticketProductId),
  );

  if (map.resource.status === 'loading') return <LoadingRegion label="Loading the room" />;
  if (map.resource.status === 'failed') return <FailureNotice error={map.resource.error} />;
  const mapView = map.resource.data;
  // The room's tables, not the configuration's. A table exists because the map
  // says so; whether it sells whole or by the seat is what this surface decides,
  // and a table nobody has configured yet still needs that choice offered.
  const mapTables = mapView.sections.flatMap((section) => section.tables);

  /** Seat keys to occurrence ids, so a canvas click resolves to what the server named. */
  const occurrenceByKey = new Map(mapView.seats.map((seat) => [seat.seatKey, seat.seatId]));
  const selectedOccurrences = [...selected]
    .map((key) => occurrenceByKey.get(key))
    .filter((id): id is string => id !== undefined);

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-row gc-row--baseline">
        <h3>Version {loaded.configurationVersionId.slice(0, 8)}</h3>
        <Status
          tone={lifecycle.tone}
          glyph={lifecycle.glyph}
          label={lifecycle.label}
          title={lifecycle.meaning}
        />
        {editable && (
          <Status tone={state.tone} glyph={state.glyph} label={state.label} title={state.meaning} />
        )}
      </div>
      <p className="gc-muted" data-testid="seating-draft-state">
        {editable ? state.meaning : lifecycle.meaning}
      </p>
      {/*
        The digest this editor will save against. Visible because a stale-write
        race is otherwise invisible until it fails, and an operator comparing two
        screens should be able to see that they are holding different versions.
      */}
      <p className="gc-muted gc-mono" data-testid="seating-digest">
        Loaded {draft.expectedDigest.slice(0, 12)}
      </p>

      {!editable && (
        <Notice tone="info" title="This configuration is live and cannot change">
          Seats can be held and bought against it, so what it says has to stay still. To change what
          sells, start a new draft.
        </Notice>
      )}

      {draft.status === 'stale' && (
        <Notice tone="critical" title="Someone else saved this seating first" live>
          <div className="gc-stack gc-stack--tight">
            <p>
              Your changes are still here and have not been sent. GateCheckPro will not write them
              over work you have not seen, and will not try to combine the two.
            </p>
            {draft.serverDocument !== null && (
              <p className="gc-muted" data-testid="seating-stale-summary">
                Theirs has{' '}
                {draft.serverDocument.seats
                  .filter((seat) => seat.saleDisposition === 'SELLABLE')
                  .length.toLocaleString()}{' '}
                seats on sale; yours has{' '}
                {draft.document.seats
                  .filter((seat) => seat.saleDisposition === 'SELLABLE')
                  .length.toLocaleString()}
                .
              </p>
            )}
            <div className="gc-row">
              <Button variant="primary" onClick={() => void draft.discardAndReload()}>
                Discard mine and load theirs
              </Button>
            </div>
          </div>
        </Notice>
      )}

      <FailureNotice error={draft.status === 'stale' ? null : draft.error} />
      <FailureNotice error={command.error} />

      <Card
        title="What sells in this room"
        eyebrow={`${summary.configuration.sellable.toLocaleString()} on sale`}
        action={
          editable ? (
            <div className="gc-row">
              <Button
                variant="primary"
                busy={draft.status === 'saving'}
                disabled={draft.status === 'persisted' || draft.status === 'saving'}
                // Only on success. Refreshing after a refused save remounts this
                // editor against the server's newer version, which resets the
                // draft and destroys the very conflict the person is being asked
                // to decide about: the save fails, the warning appears for an
                // instant, and the page then looks as though nothing happened.
                onClick={() =>
                  void draft.save().then((saved) => {
                    if (saved) onChanged();
                  })
                }
              >
                Save
              </Button>
              <Button
                busy={command.busy}
                onClick={() =>
                  void command.run(`${versionPath}/activate`, { method: 'POST' }).then((result) => {
                    if (result !== null) onChanged();
                  })
                }
              >
                Put on sale
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="gc-stack">
          <SeatingCounts summary={summary} />

          <SeatingRoomCanvas
            mapView={mapView}
            summary={summary}
            overlay={overlay}
            onDrawnSeatsChange={requestDetail}
            selected={selected}
            onSeatToggle={
              editable
                ? (seatKey) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (next.has(seatKey)) next.delete(seatKey);
                      else next.add(seatKey);
                      return next;
                    })
                : undefined
            }
          />
        </div>
      </Card>

      {editable && (
        <>
          <Card
            title="Put the selected seats on sale"
            eyebrow={`${selected.size.toLocaleString()} selected`}
          >
            <div className="gc-stack">
              <div className="gc-row">
                <Button
                  onClick={() => setSelected(new Set(mapView.seats.map((seat) => seat.seatKey)))}
                >
                  Select the whole room
                </Button>
                {mapView.sections.map((section) => (
                  <Button
                    key={section.sectionId}
                    onClick={() =>
                      setSelected(
                        new Set(
                          mapView.seats
                            .filter((seat) => seat.sectionId === section.sectionId)
                            .map((seat) => seat.seatKey),
                        ),
                      )
                    }
                  >
                    Select {section.displayName}
                  </Button>
                ))}
                <Button onClick={() => setSelected(new Set())}>Clear</Button>
              </div>

              <div className="gc-field">
                <label className="gc-field__label" htmlFor="seatingProduct">
                  Sold as
                </label>
                <select
                  id="seatingProduct"
                  className="gc-select"
                  value={productId}
                  onChange={(input) => setProductId(input.target.value)}
                >
                  <option value="">Choose a product</option>
                  {usableProducts.map(({ product }) => (
                    <option key={product.ticketProductId} value={product.ticketProductId}>
                      {product.currentVersion.displayName} ({product.code})
                    </option>
                  ))}
                </select>
                <span className="gc-field__hint">
                  The exact terms are frozen by GateCheckPro when this goes on sale.
                </span>
              </div>

              <div className="gc-row">
                <Button
                  variant="primary"
                  disabled={selected.size === 0 || productId === ''}
                  onClick={() => {
                    // The allocation set aside for *this night and this
                    // product*, not the product's general-admission pool. Those
                    // are different capacities, and confusing them would let a
                    // reserved chair draw on the standing-room count.
                    const allocation =
                      inventory.find((row) => row.ticketProductId === productId)
                        ?.inventoryAllocationId ?? null;
                    draft.edit((current) =>
                      applyToSeats(current, selectedOccurrences, {
                        saleDisposition: 'SELLABLE',
                        ticketProductId: productId,
                        inventoryAllocationId: allocation,
                        reason: null,
                      }),
                    );
                    setSelected(new Set());
                  }}
                >
                  Put {selected.size.toLocaleString()} on sale
                </Button>
                <Button
                  disabled={selected.size === 0}
                  onClick={() => {
                    draft.edit((current) =>
                      applyToSeats(current, selectedOccurrences, {
                        saleDisposition: 'EXCLUDED',
                        ticketProductId: null,
                        inventoryAllocationId: null,
                        reason: 'Held back',
                      }),
                    );
                    setSelected(new Set());
                  }}
                >
                  Keep {selected.size.toLocaleString()} off sale
                </Button>
              </div>
            </div>
          </Card>

          {mapTables.length > 0 && (
            <Card
              title="Tables"
              eyebrow={
                productId === ''
                  ? 'Choose a product above before selling a table whole'
                  : 'Sold whole, or by the seat'
              }
            >
              <div className="gc-table-scroll">
                <table className="gc-table">
                  <thead>
                    <tr>
                      <th scope="col">Table</th>
                      <th scope="col">How it sells</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mapTables.map((table) => (
                      <tr key={table.tableId}>
                        <td className="gc-mono">{table.code}</td>
                        <td>
                          <select
                            className="gc-select"
                            aria-label={`How table ${table.code} sells`}
                            value={
                              draft.document.tables.find(
                                (configured) => configured.venueMapTableId === table.tableId,
                              )?.saleMode ?? 'INDIVIDUAL_SEATS'
                            }
                            onChange={(input) =>
                              draft.edit((current) =>
                                setTableSaleMode(
                                  current,
                                  table.tableId,
                                  input.target.value as TableSaleMode,
                                  mapView.seats
                                    .filter((seat) => seat.tableId === table.tableId)
                                    .map((seat) => seat.seatId),
                                  {
                                    ticketProductId: productId === '' ? null : productId,
                                    inventoryAllocationId:
                                      inventory.find((row) => row.ticketProductId === productId)
                                        ?.inventoryAllocationId ?? null,
                                  },
                                ),
                              )
                            }
                          >
                            <option value="INDIVIDUAL_SEATS">By the seat</option>
                            <option value="WHOLE_TABLE">As a whole table</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {summary.configuration.configured === 0 && (
        <EmptyState title="Nothing configured yet">
          Select seats on the room above and choose what they are sold as.
        </EmptyState>
      )}
    </div>
  );
}
